import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/Dialog';
import { SimpleOTPInput } from '@/components/Input/SimpleOTPInput';
import { Button } from '@/components/Button';
import { usePinProtection } from '@/contexts';

const PIN_LENGTH = 4;

const ShieldIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
    />
  </svg>
);

const LockIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
    />
  </svg>
);

export const PinEntryModal: React.FC = () => {
  const navigate = useNavigate();
  const { showPinModal, setShowPinModal, setPendingRoute, isLocked, lockedUntil, verifyPin } =
    usePinProtection();

  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState<number>(0);
  const [wasVerified, setWasVerified] = useState(false);

  // Countdown timer for lockout
  useEffect(() => {
    if (lockedUntil) {
      const updateCountdown = () => {
        const remaining = Math.max(0, Math.ceil((lockedUntil.getTime() - Date.now()) / 1000));
        setCountdown(remaining);
        if (remaining <= 0) {
          setError(null);
        }
      };
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [lockedUntil]);

  const handleSubmit = async () => {
    if (pin.length !== PIN_LENGTH || isLoading) return;

    setIsLoading(true);
    setError(null);

    const result = await verifyPin(pin);

    setIsLoading(false);

    if (result.success) {
      setWasVerified(true);
      setShowPinModal(false);
      setPendingRoute(null);
      setPin('');
      setRemainingAttempts(null);
      setError(null);
      // Content will render automatically since isVerified is now true
    } else {
      setPin('');
      if (result.locked_until) {
        setError('Cuenta bloqueada temporalmente');
      } else {
        setRemainingAttempts(result.remaining_attempts || null);
        setError(`PIN incorrecto`);
      }
    }
  };

  // Auto-submit when PIN is complete
  useEffect(() => {
    if (pin.length === PIN_LENGTH && !isLoading) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const handleClose = () => {
    // Only navigate away if user cancelled (not after successful verification)
    if (!wasVerified) {
      navigate('/');
    }
    setShowPinModal(false);
    setPendingRoute(null);
    setPin('');
    setError(null);
    setRemainingAttempts(null);
    setWasVerified(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog
      open={showPinModal}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      {/* No se descarta con Escape ni tocando afuera: la única salida sin PIN es
          «Cancelar» / «Volver al inicio» (que vuelven al inicio). */}
      <DialogContent
        size="sm"
        mobile="sheet"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        {isLocked && countdown > 0 ? (
          /* Locked State */
          <div className="text-center space-y-6">
            {/* Lock Icon */}
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-2xl bg-linear-to-br from-frost-coral to-frost-coral-deep flex items-center justify-center shadow-frost-md">
                <LockIcon />
                <svg
                  className="w-10 h-10 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
              </div>
            </div>

            <div className="space-y-2">
              <DialogTitle className="text-xl font-semibold text-frost-ink">Acceso bloqueado</DialogTitle>
              <DialogDescription className="text-frost-ink-mid text-sm">
                Demasiados intentos fallidos
              </DialogDescription>
            </div>

            {/* Countdown Timer */}
            <div className="bg-frost-paper rounded-2xl p-6">
              <p className="text-frost-ink-mid text-xs uppercase tracking-wider mb-2">Tiempo restante</p>
              <div className="text-4xl font-light text-frost-ink font-mono tracking-wider">
                {formatTime(countdown)}
              </div>
            </div>

            <div className="pt-2">
              <Button variant="outline" onClick={handleClose} className="w-full">
                Volver al inicio
              </Button>
            </div>

            <p className="text-xs text-frost-ink-whisper">
              ¿Olvidaste tu PIN?{' '}
              <a
                href="mailto:soporte@agora.red"
                className="text-frost-ink-soft hover:text-frost-ink transition-colors"
              >
                Contacta a soporte
              </a>
            </p>
          </div>
        ) : (
          /* PIN Entry State */
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-frost-ink-soft to-frost-ink flex items-center justify-center text-white shadow-frost-md">
                  <ShieldIcon />
                </div>
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-xl font-semibold text-frost-ink">
                  Verificación requerida
                </DialogTitle>
                <DialogDescription className="text-frost-ink-mid text-sm">
                  Ingresa tu PIN de 4 dígitos para continuar
                </DialogDescription>
              </div>
            </div>

            {/* PIN Input */}
            <div className="flex justify-center py-2">
              <SimpleOTPInput
                maxLength={PIN_LENGTH}
                value={pin}
                onChange={(value) => setPin(value)}
                mask
                className="gap-3"
                slotClassName="!w-14 !h-14 !text-2xl !rounded-xl !border-frost-hair-stronger focus:!border-frost-brand focus:!ring-frost-brand/20"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-xl border border-frost-coral/25 bg-frost-card backdrop-blur-[14px] p-3 text-center">
                <p className="text-frost-coral-text text-sm font-semibold">{error}</p>
                {remainingAttempts !== null && (
                  <p className="text-frost-ink-soft text-xs mt-1">
                    {remainingAttempts} {remainingAttempts === 1 ? 'intento restante' : 'intentos restantes'}
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3 pt-2">
              <Button
                onClick={handleSubmit}
                disabled={pin.length !== PIN_LENGTH || isLoading}
                isLoading={isLoading}
                className="w-full !bg-frost-ink hover:!bg-frost-ink-soft !text-white"
              >
                Verificar PIN
              </Button>
              <Button
                variant="outline"
                onClick={handleClose}
                className="w-full !border-frost-hair-stronger !text-frost-ink-soft"
              >
                Cancelar
              </Button>
            </div>

            {/* Support Link */}
            <p className="text-center text-xs text-frost-ink-whisper pt-2">
              ¿Olvidaste tu PIN?{' '}
              <a
                href="mailto:soporte@agora.red"
                className="text-frost-ink-soft hover:text-frost-ink transition-colors"
              >
                Contacta a soporte
              </a>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
